import './TaskItemChooser.less';
import TaskTemplates from './TemplateTasks/TaskTemplates';
import TaskItem from './TaskItem';
import {CancelFunction} from "../@types/functions";
import TemplateTask from "../entities/templateTask";
import Task from "../entities/task";
import {stripTemplateHtml} from "./TaskHelper";
import {EditableTableCardProps} from "../ui/EditableTable/EditableTable";
import useComponentCache from "../hooks/useComponentCache";
import {useEffect, useRef, useState} from "react";

const SELECTION = 'S';
const EDITING = 'E';

type TaskItemChooserState = {
    task: Task | null,
    stage: string,
}

/**
 * Component to render either a list of TaskTemplates, or a TaskItem.
 */
function TaskItemChooser(props: TaskItemChooserProps) {
	const {cacheSave, cacheValue, cacheReset} = useComponentCache<TaskItemChooserState>(props.keyName);

	const [task, setTask] = useState<Task>(cacheValue('task', null));
	const [stage, setStage] = useState<string>(cacheValue('stage', SELECTION)); // SELECTION, EDITING

    const doCancelRef = useRef<boolean>(false);

    useEffect(() => {
        const doCancel = doCancelRef.current;

        return () => {
            if (doCancel) cacheReset();
            else cacheSave({task, stage});
        }
    }, [cacheReset, cacheSave, stage, task]);

	/**
	 * The user has clicked on one of the Task Templates.  The template will be passed to us, and we need to associate
	 * it with the matter/lead/campaign that the AssociatedTasks component is within.
	 *
	 * @param {Task} templateTask
	 */
	const onTemplateClick = (templateTask: TemplateTask) => {
		const task = stripTemplateHtml(new Task(templateTask));

		// Update task with parentage
		switch (props.env.topLevel) {
			case 'matter':
				task.tieIns = [{module: 'Client', entity: 'Matter', id: props.env.topLevelId}];
				break;
			case 'lead':
				task.tieIns = [{module: 'Sales', entity: 'Lead', id: props.env.topLevelId}];
				break;
			case 'campaign':
				task.tieIns = [{module: 'Marketing', entity: 'Campaign', id: props.env.topLevelId}];
				break;
			default:
		}

		// Convert from template to task
		task.entityType = 'task';

		// Set the task
		setTask(task);

		// Set the stage to editing
		setStage(EDITING);
	}

	/**
	 * Handle passing of task to onOk handler (from TaskItem).  Sent up the chain to AssociatedTasks.
	 *
	 * @param {Task|Task[]} task
	 */
	const onOk = (task: Task) => {
		if (Array.isArray(task)) console.error('Got an array in TaskItem.onOk?');

		const taskClone = (Array.isArray(task) ? task[0] : task).clone();

		taskClone.persisted = false;

		// Clear the cache
		doCancelRef.current = true;

		// Clear out the task
		setTask(null);

		// Set the stage back to selection
		setStage(SELECTION);

		// Call the handler
		setTimeout(() => props.onOk?.(taskClone), 1);
	}

	/**
	 * Canceled out of a TaskItem.  Send up the chain to AssociatedTasks.
	 */
	const onCancel = () => {
		// Clear the cache
		doCancelRef.current = true;

		// Clear out the task
		setTask(null);

		// Set the stage back to selection
		setStage(SELECTION);

		// Call the handler
		setTimeout(() => props.onCancel?.(), 1);
	}

	const card = props.param !== 'template'
				 ? <TaskItem {...props} onOk={onOk} onCancel={onCancel}/>
				 : task
				   ? <TaskItem {...props}
				               current={task}
				               onOk={onOk}
				               onCancel={onCancel}
				   />
				   : <TaskTemplates {...props} // includes keyName
									useChooser={true}
									onClick={onTemplateClick}
									onCancel={onCancel}
				   />;

	return <div style={{height: '100%', width: '100%'}}>{card}</div>
}

export type TaskItemChooserProps = Partial<EditableTableCardProps<Task>> & {
	// Unique key required for TaskTemplates
	keyName: string,

	// What to create:  task or template
	param: string,

	// Other things to pass through, like limitToAreas
	env: any,

	// Sent from EditableTable
	onCancel: CancelFunction,
};

export default TaskItemChooser;
